import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import ThemeButtonMenuItem from 'assets/components/button/themes/Theme.Button.MenuItem';
import DataMap from 'assets/components/dataMap/DataMap';
import { addLog } from 'assets/components/feedback/Feedback';
import Icon from 'assets/components/icon/Icon';
import Confirm from 'assets/components/page/templates/Confirm';
import ThemeConfirmDefault from 'assets/components/page/templates/themes/Theme.Confirm.Default';
import useApi from 'assets/hooks/api/useApi';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import DropdownButton from 'assets/templates/dropdownButton/DropdownButton';
import ThemeDropdownButtonPopup from 'assets/templates/dropdownButton/themes/Theme.DropdownButton.Popup';
import { combineStrings } from 'assets/utils/data/String';
import { ApiHeaderApplication, HttpStatus } from 'config/Api.Config';
import { AuthPermission } from 'config/Auth.Config';
import { compact, isEmpty, merge } from 'lodash';
import Claim from 'models/claims/claim/Model.Claim';
import EmailTemplate from 'models/core/claimEmailTemplate/Model.ClaimEmailTemplate';
import Permission from 'models/core/permission/Model.Permission';
import User from 'models/core/user/Model.User';
import { getNextStatus } from 'module/claims/hooks/useClaimsGlobalStore';
import { useEffect, useState } from 'react';

type Props = {
  emailTemplates: EmailTemplate[];
  statusInfo: Model.ClaimStatusInfo;
  claim: Model.IClaim;
  currentPermissions: Auth.Permission[];
  reloadClaim: () => void;
  hasPermission: boolean;
  canTakeOwnership: boolean;
};

export default function ClaimChangeOwnership({
  emailTemplates,
  statusInfo,
  currentPermissions,
  claim,
  reloadClaim,
  hasPermission,
  canTakeOwnership,
}: Props) {
  const { lang } = getLocales();
  const { user, permissions } = useAuthStore();
  const [selectedUser, setSelectedUser] = useState<Model.IUser>(undefined);
  const { nextStatusList } = getNextStatus(emailTemplates, statusInfo, currentPermissions, claim?.statusCode);
  const isClaimAdmin = permissions?.CLAIM_ADMIN;

  const userListApi = useApi({
    action: User.list,
    body: {
      isDisabled: false,
      distributorId: user?.distributorId ? [user?.distributorId] : undefined,
      dealerId: compact(user.dealers.map((it) => it.id)),
    },
    wait: true,
  });
  const permissionListApi = useApi({
    action: Permission.list,
    body: {
      applicationId: ApiHeaderApplication.claims,
    },
    callback: (req, res) => {
      const applicationId = [req.applicationId];
      if (res?.status === HttpStatus.ok) {
        const permissionId = res?.payload?.data
          ?.filter((it) => it.key === AuthPermission.claimAdmin || it.key === AuthPermission.updateClaimOwner)
          ?.map((it) => it.id);

        if (!isEmpty(permissionId)) {
          userListApi.execute((b) => ({ ...b, permissionId, applicationId }));
        }
      } else {
        userListApi.execute((b) => ({ ...b, applicationId }));
      }
    },
  });

  useEffect(() => {
    if (hasPermission) permissionListApi.execute((b) => b);
  }, [hasPermission]);

  async function onConfirmChangeOwnership() {
    if (selectedUser && nextStatusList?.length > 0) {
      const { status, message } = await Claim.changeOwnership({
        claimId: claim?.id,
        ownerId: selectedUser.id,
      });
      if (status !== HttpStatus.ok) addLog({ error: message });
      else reloadClaim();
      setSelectedUser(undefined);
    }
  }
  const showTakeOwnership = !claim?.ownerId && !isClaimAdmin;
  const showChangeOwner = isClaimAdmin || !!claim?.ownerId;

  return (
    <>
      {showTakeOwnership && (
        <Button
          class={ThemeButtonCircle}
          render={lang.takeOwnershipOfTheClaim}
          onClick={setSelectedUser}
          data={user}
          disabled={canTakeOwnership}
          media={<Icon class="fas-user" />}
        />
      )}

      {showChangeOwner && (
        <DropdownButton
          title={lang.transferOwnershipTo}
          buttonBody={lang.changeOwner}
          buttonMedia={<Icon class="fas-user" />}
          horizontalPosition="auto_fixed"
          verticalPosition="auto_fixed"
          constrainment="constrained"
          tabIndex={null}
          disabled={hasPermission}
          htmlElementProps={{ title: lang.changeOwner }}
          subcomponents={merge(ThemeDropdownButtonPopup, {
            dropdown: {
              style: {
                maxHeight: '372px',
              },
            },
          })}
          dropdownOptions={
            <DataMap
              data={userListApi?.payload?.data}
              render={({ data: user }) => (
                <Button
                  key={user?.id}
                  class={ThemeButtonMenuItem}
                  render={user?.displayInfo?.title}
                  onClick={setSelectedUser}
                  data={user}
                />
              )}
            />
          }
        />
      )}

      {selectedUser && (
        <Confirm
          data={claim}
          onConfirm={onConfirmChangeOwnership}
          onClose={() => setSelectedUser(undefined)}
          actionName={showTakeOwnership ? lang.takeOwnershipOfTheClaim : lang.transferOwnershipTo}
          itemName={showChangeOwner ? combineStrings(' ', selectedUser.firstName, selectedUser.lastName) : null}
          confirmationType="nextButton"
          subcomponents={ThemeConfirmDefault}
        />
      )}
    </>
  );
}
